import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../../lib/i18n";

export const Header = () => {
  const { t, locale, setLocale } = useI18n();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/about", label: t.nav.about },
    { to: "/portfolio", label: t.nav.portfolio },
    { to: "/blog", label: t.nav.blog || "Blog" },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.06]"
    >
      <div className="px-4 sm:px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 gap-2">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 sm:gap-3 group min-w-0 flex-shrink">
          <img
            src="/jf-logo.png"
            srcSet="/jf-logo-192.png 1x, /jf-logo.png 2x"
            alt="Joshi FusionTech Media"
            width="40"
            height="40"
            className="w-9 h-9 sm:w-10 sm:h-10 shrink-0"
            loading="eager"
            decoding="async"
          />
          <div className="leading-tight min-w-0">
            <div className="font-display font-extrabold text-white tracking-tight text-[13px] sm:text-[15px] md:text-base whitespace-nowrap">
              JOSHI <span className="text-brand-orange">FUSIONTECH</span> MEDIA
            </div>
            <div className="hidden sm:block font-mono text-[10px] tracking-[0.28em] text-white/55 uppercase mt-0.5 whitespace-nowrap">
              Automate · Innovate · Elevate
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-1 text-xs font-mono">
            <button
              data-testid="locale-en"
              onClick={() => setLocale("en")}
              className={`px-2 py-1 rounded transition-colors ${locale === "en" ? "text-white bg-white/10" : "text-white/40 hover:text-white/70"}`}
            >EN</button>
            <span className="text-white/20">/</span>
            <button
              data-testid="locale-de"
              onClick={() => setLocale("de")}
              className={`px-2 py-1 rounded transition-colors ${locale === "de" ? "text-white bg-white/10" : "text-white/40 hover:text-white/70"}`}
            >DE</button>
          </div>
          <Link to="/book" data-testid="header-cta" className="btn-primary text-sm py-2.5 px-5">
            {t.nav.book}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-nav" className="lg:hidden border-t border-white/[0.06] bg-[#05050A]/95 backdrop-blur-2xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-white" : "text-white/60"}`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 text-xs font-mono pt-2">
              <button onClick={() => setLocale("en")} className={`px-3 py-1.5 rounded ${locale === "en" ? "bg-white/10 text-white" : "text-white/50"}`}>EN</button>
              <button onClick={() => setLocale("de")} className={`px-3 py-1.5 rounded ${locale === "de" ? "bg-white/10 text-white" : "text-white/50"}`}>DE</button>
            </div>
            <Link to="/book" data-testid="mobile-cta" className="btn-primary mt-2">
              {t.nav.book}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
